import { CUSTOM_EXPIRY_TIME } from "@/constants/campaignBuilder";
import {
  CREDIT_INCREMENT,
  LAUNCH_BLOCKER_TEXT,
  MIN_PAYG_BUDGET,
  MIN_PAYG_RECIPIENTS,
  PAYG_RATE,
} from "@/constants/review";
import type { ExpiryMode } from "@/context/CampaignBuilderContext";
import type { CostBreakdown, PostpaidSplit } from "@/interfaces/review";
import { zonedWallTimeToIso } from "@/utils/timezone-helper";

/**
 * Computes the Step 4 buy-box numbers. Mirrors the Stage 4 prototype's `_paygSplit()` and
 * `_postLaunchFunding()` (Campaign-Builder-Master-Prototype-Stage4-Revisions-08-22-26.html).
 *
 * THE DEPOSIT IS A SHARE OF THE GROSS SUBTOTAL, not of the post-credit total — this is the one
 * thing to get right here, and the previous version had it the other way round. Applying credits
 * first and then taking 40% shrinks the deposit itself, which is not what "credits pay down the
 * deposit" means. On a $500 order with $250 of credit:
 *
 *   wrong   total 250 → due today 100      (40% of what is left after credits)
 *   right   deposit 200 → credit clears it → due today 0, and 250 carries to the later balance
 *
 * It matters beyond the arithmetic: the deposit IS the exposure control for the maintenance-margin
 * model, so under-collecting it under-funds the campaign from the first day.
 *
 * Credits are capped at the deposit while pay-as-you-give is on: only the deposit is being settled
 * now, so that is all the wallet is debited for. The rest is not stranded — it is drawn down before
 * the card as gifts are redeemed, which is what `potentialCardSpend` reports.
 *
 * No service fee (deleted in the Stage 2 revisions, so `subtotal === giftBudget`), and credits are
 * all-or-nothing rather than a partial amount.
 */
/** Round UP to the next whole gift. Falls back to the raw amount when there is no gift size yet. */
function wholeGifts(amount: number, perGift: number): number {
  if (amount <= 0) return 0;
  if (!Number.isFinite(perGift) || perGift <= 0) return amount;
  return Math.ceil(amount / perGift) * perGift;
}

export function computeCostBreakdown(input: {
  budgetPerRecipient: number | null;
  recipientCount: number;
  creditsBalance: number;
  applyAllCredits: boolean;
  payAsYouGive: boolean;
}): CostBreakdown {
  const { budgetPerRecipient, recipientCount, creditsBalance, applyAllCredits, payAsYouGive } =
    input;

  const tier = Number(budgetPerRecipient) || 0;
  const giftBudget = tier * recipientCount;
  const subtotal = giftBudget;

  // Gross, never post-credit. Equal to the whole subtotal when pay-as-you-give is off.
  //
  // And held in WHOLE GIFTS: the percentage is rounded UP to the next multiple of one gift's
  // budget, mirroring `minimumBalanceFor` on the API. Redemptions are indivisible, so a deposit
  // that is a fraction of a gift cannot honour even the first one — 1 × $500 at 40% is $200, and
  // that recipient's link would be refused as `budget_exhausted`. At scale it barely moves (400 ×
  // $500 at 40% is already an exact multiple); it is the small end this corrects.
  const deposit = payAsYouGive ? wholeGifts(subtotal * PAYG_RATE, tier) : subtotal;

  // Checkout only settles the deposit, so that is the ceiling on what the wallet is debited today.
  const creditsMax = Math.min(creditsBalance, Math.max(0, deposit));
  // The client's choice, not a formality (Judd, 2026-08-25): opted out, the launch buys the whole
  // requirement on the card and the wallet is left alone. `creditsToBuy` below is what that costs.
  const creditsApplied = applyAllCredits ? creditsMax : 0;

  const creditsToDeposit = Math.min(creditsApplied, deposit);
  const dueToday = Math.max(0, deposit - creditsToDeposit);

  // Whatever credit is left over reduces the 60% that bills as gifts redeem. The spill-over term is
  // defensive: creditsMax already caps at the deposit, so it should always be zero.
  const laterBalance = payAsYouGive
    ? Math.max(0, subtotal - deposit - (creditsApplied - creditsToDeposit))
    : 0;

  // Post-launch, redemptions draw whatever credit is still in the wallet before the card is
  // charged — so only the remainder is exposed to the card.
  const creditLeft = Math.max(0, creditsBalance - creditsApplied);
  const laterCoveredByCredit = Math.min(creditLeft, laterBalance);
  const potentialCardSpend = Math.max(0, laterBalance - laterCoveredByCredit);

  return {
    recipientCount,
    budgetPerRecipient: tier,
    giftBudget,
    subtotal,
    creditsToPurchase: Math.round(giftBudget),
    creditsMax,
    creditsApplied,
    // WHAT THE CARD PAYS, now plus later — not `subtotal − creditsApplied`.
    //
    // Under pay-as-you-give `creditsApplied` is capped at the deposit, so subtracting it from the
    // whole subtotal ignores the credit that covers the REST as gifts are redeemed. A wallet
    // holding $4,900 against a $500 campaign reported "Estimated Total $300" while both rows below
    // it read $0 — the total contradicted its own breakdown.
    //
    // Prepaid is unchanged: `dueToday` is already `subtotal − creditsApplied` there and
    // `potentialCardSpend` is zero, so one expression now serves both models.
    total: dueToday + potentialCardSpend,
    deposit,
    dueToday,
    laterBalance,
    laterCoveredByCredit,
    potentialCardSpend,
    // What launch will buy on the card, rounded UP to a mintable increment exactly as the API does
    // (`roundUpToIncrement`) — so the dialog quotes the figure that is actually charged rather than
    // one a few dollars under it.
    creditsToBuy: dueToday > 0 ? Math.ceil(dueToday / CREDIT_INCREMENT) * CREDIT_INCREMENT : 0,
    payAsYouGive,
  };
}

/**
 * Is this campaign big enough for pay-as-you-give?
 *
 * BOTH floors bind, both INCLUSIVE: 50 recipients at exactly 5,000 credits qualifies. The server
 * runs the same test in `quoteLaunch` (api: services/campaignLaunchFunding.ts) and stays the
 * authority — this exists so the switch can refuse before a deposit is priced, not so the refusal
 * moves here.
 *
 * Takes the tier and the count rather than a subtotal, and derives the budget the same way
 * `computeCostBreakdown` does, so the two can never read a campaign as two different sizes. The
 * API sums each contact's own price; the builder has one tier for everyone, so the products agree.
 */
export function paygSizeEligible(input: {
  budgetPerRecipient: number | null;
  recipientCount: number;
}): boolean {
  const giftBudget = (Number(input.budgetPerRecipient) || 0) * input.recipientCount;
  return input.recipientCount >= MIN_PAYG_RECIPIENTS && round2(giftBudget) >= MIN_PAYG_BUDGET;
}

export interface LaunchGateInput {
  recipientCount: number;
  /**
   * The Step 1 budget tier. Zero/absent when the user reached Step 4 without picking one —
   * possible because a page refresh clears the builder context and the stepper allows jumping
   * straight back to Review. Without this gate the CTA armed and the server refused the launch
   * with "creditsPerContact must be a positive number" (found in browser testing, DIV-7811).
   */
  budgetPerRecipient: number;
  /**
   * What the wallet can put behind THIS campaign — credits that outlive it, less the headroom its
   * siblings already hold. `useCampaignCredits` computes it; it is not the raw wallet balance.
   */
  creditsBalance: number;
  /**
   * What must be funded before the campaign may open: the whole subtotal when prepaid, the
   * deposit when pay-as-you-give. The two models differ ONLY in this number.
   */
  fundingRequired: number;
  payAsYouGive: boolean;
  /**
   * The client opted in to spending their existing credits. Opted OUT, the wallet counts for
   * nothing here — exactly as it counts for nothing in `fundLaunch` — so the whole requirement is
   * a card purchase however large the balance is.
   */
  applyExistingCredits: boolean;
  /** A card is nominated for the campaign. Only the maintenance-margin model needs one. */
  hasPaymentMethod: boolean;
  /**
   * `scheduleMode === "certain"` and that send instant is at most 60 minutes away (or past) —
   * computed by the caller.
   */
  schedulePast: boolean;
  /** `scheduleMode === "certain"` with no date picked at all — computed by the caller (GFG-293). */
  scheduleMissing: boolean;
}

/**
 * Why the campaign cannot launch yet, or null when it can. One function so the CTA's enabled
 * state, the expiry acknowledgment and the footer sentence can never disagree about the reason.
 *
 * The two funding models ask different questions, and the difference is the whole point:
 *
 *   prepaid    credits must cover the campaign outright; pay-as-you-give, only the deposit.
 *
 * Neither is a REASON TO REFUSE. Launch buys whatever the wallet is short on the campaign's card,
 * so being under-funded is what the checkout is for, not a reason to disable it. The gate is
 * therefore on the CARD: no card, nothing to buy credits with. Pay-as-you-give needs one whether
 * the wallet is short or not, because its later margin calls have nothing else to charge.
 *
 * Deliberately NOT a function of the apply-credits checkbox. An earlier version keyed readiness off
 * the total, which made that checkbox a hidden switch for the whole buy box — ticking it zeroed the
 * total, which armed the CTA while "Add Payment Method" was still on screen (removed on review,
 * Ivan, 2026-08-14). Reading the BALANCE instead keeps the checkbox to what it says it does: it
 * moves money, it does not decide whether the campaign may open.
 *
 * The expiry acknowledgment is deliberately absent — the prototype leaves the CTA enabled and
 * raises an inline error beside the checkbox when clicked unacknowledged (`openCampaignCheckout()`
 * ~line 23985). So is the prototype's `!!charity` test, which is vacuous in practice.
 */
export type LaunchBlocker =
  | "no_recipients"
  | "no_budget"
  | "schedule_missing"
  | "schedule_in_past"
  | "no_payment_method";

export function launchBlocker(input: LaunchGateInput): LaunchBlocker | null {
  const {
    recipientCount,
    budgetPerRecipient,
    creditsBalance,
    fundingRequired,
    payAsYouGive,
    applyExistingCredits,
    hasPaymentMethod,
    schedulePast,
    scheduleMissing,
  } = input;

  if (recipientCount <= 0) return "no_recipients";
  if (budgetPerRecipient <= 0) return "no_budget";
  // A missing date is checked before a past one: there is no date to be past.
  if (scheduleMissing) return "schedule_missing";
  if (schedulePast) return "schedule_in_past";
  // Short of credits is NOT a blocker — launch buys the difference on the campaign's card. What it
  // does require is a card to buy them WITH. Rounded to cents before comparing: the balance is a
  // decimal string and the requirement a float product, so a campaign covered to the penny must
  // not be refused by a 1e-13 residue.
  // The SAME arithmetic the server runs in `fundLaunch`. Reading the raw balance here while the
  // server reads zero is what let a launch arm with no card: a $4,900 wallet against a $200
  // campaign looked covered, while the unticked opt-in meant every one of those 200 credits was
  // going to be bought.
  const usableCredits = applyExistingCredits ? creditsBalance : 0;
  const mustBuy = round2(usableCredits) < round2(fundingRequired);
  // Pay-as-you-give needs one either way — its later margin calls have nothing else to charge.
  if ((mustBuy || payAsYouGive) && !hasPaymentMethod) return "no_payment_method";
  return null;
}

/** Drives both the CTA's enabled state and whether the expiry acknowledgment is shown. */
export function isLaunchReady(input: LaunchGateInput): boolean {
  return launchBlocker(input) === null;
}

/** The footer sentence for a blocker. `null` and "no_recipients" have their own UI and stay silent. */
export function launchBlockerText(blocker: LaunchBlocker | null): string | undefined {
  if (blocker === null || blocker === "no_recipients") return undefined;
  return LAUNCH_BLOCKER_TEXT[blocker];
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** How many credits short the wallet is — the number the funding row asks the user to close. */
export function creditShortfall(input: {
  creditsBalance: number;
  fundingRequired: number;
}): number {
  return Math.max(0, round2(input.fundingRequired - input.creditsBalance));
}

export const LAUNCH_BUTTON_LABEL = {
  sendNow: "Send Gifts Now",
  schedule: "Schedule Gifts",
} as const;

/**
 * The CTA label follows the SCHEDULE, not readiness: ASAP sends read "Send
 * Gifts Now", anything dated reads "Schedule Gifts". The prototype derives
 * this by scraping the selected schedule radio (`_isSendAsap()` ~line 23807,
 * applied at ~line 23893); this port reads `scheduleMode` from the builder
 * context instead, where "certain" (one date for everyone) and "different"
 * (a date per recipient) are both scheduled sends.
 */
export function launchButtonLabel(sendAsap: boolean): string {
  return sendAsap ? LAUNCH_BUTTON_LABEL.sendNow : LAUNCH_BUTTON_LABEL.schedule;
}

/**
 * Fallback when a "custom" expiry has no date picked — matches the builder's default
 * `expiryMode` of 60 days, so an empty picker degrades to the same window the step opened with.
 */
const FALLBACK_EXPIRY_DAYS = 60;

const MS_PER_DAY = 86_400_000;

/**
 * The expiry window as a whole number of days — what `POST /campaigns/launch` stores as
 * `campaigns.contact_expire_cadence` and what every commitment's expiry is derived from.
 *
 * The 30/60/90 modes are already day counts; "custom" is measured from now to the picked date
 * via `campaignCloseDate()`, floored at 1 so a same-day close still yields a valid window.
 */
export function campaignExpiryDays(
  expiryMode: ExpiryMode,
  customExpiryDate: string,
  timeZone: string,
): number {
  if (expiryMode === "30" || expiryMode === "60" || expiryMode === "90") {
    const days = Number(expiryMode);
    return Number.isFinite(days) && days > 0 ? days : FALLBACK_EXPIRY_DAYS;
  }
  // "6mo" (calendar months) and "custom" are measured to their close date, then back to a day count.
  const closesOn = campaignCloseDate(expiryMode, customExpiryDate, timeZone);
  if (!closesOn) return FALLBACK_EXPIRY_DAYS;
  // NEVER NaN. Callers feed this straight into `new Date(Date.now() + days * MS_PER_DAY)`, and a
  // NaN there is a RangeError that takes the whole checkout column down on render — which is how
  // a malformed stored expiry (a timestamp where a date was expected) crashed Step 4. Degrading to
  // the default window shows a wrong number; throwing shows nothing at all.
  const days = Math.ceil((closesOn.getTime() - Date.now()) / MS_PER_DAY);
  return Number.isFinite(days) ? Math.max(1, days) : FALLBACK_EXPIRY_DAYS;
}

const ACK_LEAD = "I understand that any unredeemed gift credits from this campaign ";

// The prototype's `_formatExpirationDate()` (~lines 15495-15501) builds the
// string from a hardcoded English month table — `months[d.getMonth()] + ' ' +
// d.getDate() + ', ' + d.getFullYear()`, i.e. "June 24, 2026" — so it is a full
// month name in US order whatever the browser's locale. The locale is pinned
// here for the same reason: this is fixed English copy, and passing `undefined`
// would let a non-US locale reorder or translate the date.
const ACK_DATE_LOCALE = "en-US";

const ACK_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: "long",
  day: "numeric",
  year: "numeric",
};

/**
 * The campaign's close date. Mirrors the prototype's
 * `_getReminderExpirationDate()` (~lines 15467-15492), which has TWO
 * date-producing branches:
 *
 * - "custom" — the literal date the user picked, or null when the picker is
 *   empty (the prototype's `return null` after the input check).
 * - "N days from send date" — TODAY plus N days. The prototype pulls the number
 *   straight out of the radio label (`label.textContent.match(/(\d+)\s*days?/)`)
 *   and counts from `new Date()`; its own comment calls this "prototype approx"
 *   because the real send date is never consulted. This port keeps that
 *   behaviour rather than inventing a send-date-relative one.
 */
function campaignCloseDate(
  expiryMode: ExpiryMode,
  customExpiryDate: string,
  timeZone: string,
): Date | null {
  if (expiryMode === "custom") {
    if (!customExpiryDate) return null;
    // 7:00 PM in the CAMPAIGN's zone — the instant the API stores — not the browser's. Anything but
    // a `YYYY-MM-DD` (a full timestamp round-tripped from storage) is null, which every caller handles.
    const iso = zonedWallTimeToIso(customExpiryDate, CUSTOM_EXPIRY_TIME, timeZone);
    return iso ? new Date(iso) : null;
  }

  const closesOn = new Date();
  if (expiryMode === "6mo") {
    // Calendar months, matching the Expiration accordion + the server's addMonths.
    closesOn.setMonth(closesOn.getMonth() + 6);
  } else {
    closesOn.setDate(closesOn.getDate() + Number(expiryMode));
  }
  return closesOn;
}

/**
 * The expiry acknowledgment copy. Mirrors the prototype's `renderAckText()`
 * (~lines 23817-23832): the wording is driven by the ROLLOVER setting, not the
 * billing mode, and names a concrete close date whenever one can be derived.
 *
 * Every expiry mode yields a date — the 30 / 60 / 90-day ones as today plus
 * that many days, see `campaignCloseDate()` — so the undated clause is reached
 * only for a "custom" expiry whose date picker is empty, which is exactly when
 * `_getReminderExpirationDate()` returns null.
 */
export function expiryAckText(input: {
  rolloverEnabled: boolean;
  expiryMode: ExpiryMode;
  customExpiryDate: string;
  /** The campaign's Schedule zone — the close date is the day it is THERE. */
  timeZone: string;
}): string {
  const { rolloverEnabled, expiryMode, customExpiryDate, timeZone } = input;

  if (rolloverEnabled) {
    return `${ACK_LEAD}will return to my credit wallet when this campaign closes. If these credits remain unused for up to 12 months from purchase date, they will be sent to the beneficiary causes I have selected.`;
  }

  const closesOn = campaignCloseDate(expiryMode, customExpiryDate, timeZone);

  const whenClause = closesOn
    ? `will expire when this campaign closes on ${closesOn.toLocaleDateString(ACK_DATE_LOCALE, { ...ACK_DATE_FORMAT, timeZone })}.`
    : "will expire when this campaign closes.";

  return `${ACK_LEAD}${whenClause} Their value will be sent by Gifts for Good to the causes I have selected.`;
}

/**
 * How a postpaid budget divides — the rule `postpaidSplit` applies on the API, repeated so the buy
 * box shows the figures launch will write. The deposit is a share of the WHOLE budget, in whole
 * gifts rounded up; existing credits pay part of it and never shrink the budget it is measured on.
 * The server stays the authority.
 */
export function computePostpaidSplit(input: {
  budgetPerRecipient: number | null;
  recipientCount: number;
  creditsBalance: number;
  applyAllCredits: boolean;
  minUpfrontPc: number;
}): PostpaidSplit {
  const tier = Number(input.budgetPerRecipient) || 0;
  const budget = tier * input.recipientCount;
  const deposit = Math.min(wholeGifts((input.minUpfrontPc / 100) * budget, tier), budget);
  const creditsApplied = input.applyAllCredits
    ? Math.min(Math.max(input.creditsBalance, 0), deposit)
    : 0;
  return {
    budget,
    creditsApplied,
    deposit,
    dueToday: deposit - creditsApplied,
    ceiling: budget - deposit,
  };
}

/** "13 Feb 2027" — the date format the postpaid terms are stated in. */
export function termsDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** The day `days` after `iso`, as an ISO instant. */
export function addDaysIso(iso: string, days: number): string {
  return new Date(new Date(iso).getTime() + days * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * The acceptance wording, built from the figures on screen. What is shown is what is recorded —
 * the launch sends this exact string.
 */
export function postpaidAcceptanceLabel(input: {
  deposit: string;
  finalIssued: string;
  finalDue: string;
  payBy: string;
}): string {
  return `I accept the payment terms for this campaign — a ${input.deposit} deposit today, and an invoice issued ${input.finalIssued} for the gifts claimed, due ${input.finalDue} and payable by ${input.payBy}, under the Gifts for Good Terms and Conditions.`;
}

/** How an ExpectedPaymentMethod index reads on the postpaid terms. */
export function postpaidPaymentMethodLabel(method: number): string {
  return ["ACH", "Wire", "Card", "Check"][method] ?? "ACH";
}
